import { useTranslation } from "react-i18next";
import { Scaffold } from "../shell/Scaffold";
import { Navbar } from "../shell/Navbar";
import { Display, Label, Title } from "@acdc2/ui/components/access-typography";
import { useSettingsScreenSuspenseQuery } from "../client/generated";
import { Input } from "@acdc2/ui/components/input";
import { useProtectedImageSource } from "../client/images";
import { Button } from "@acdc2/ui/components/button";
import { Icon } from "@mdi/react";
import { mdiOpenInNew } from "@mdi/js";
import { Separator } from "@acdc2/ui/components/separator";
import { ScrollArea } from "@acdc2/ui/components/scroll-area";
import {
  Form,
  FormControl,
  FormField,
  FormHelpText,
  FormLabel,
} from "@acdc2/ui/components/access-form";

export function SettingsScreen() {
  const { t } = useTranslation();
  const { data } = useSettingsScreenSuspenseQuery();
  const src = useProtectedImageSource(data.viewer.avatarUrl);

  return (
    <Scaffold navbar={<Navbar />}>
      <ScrollArea>
        <div className="p-8 flex flex-col gap-8">
          <Display asChild>
            <h1>{t("SettingsScreen.title")}</h1>
          </Display>

          <div className="max-w-2xl flex flex-col gap-8">
            <Form className="flex flex-col gap-8">
              <div>
                <Title asChild>
                  <h2>{t("SettingsScreen.profile.title")}</h2>
                </Title>
                <Label>{t("SettingsScreen.profile.description")}</Label>
              </div>
              <FormField name="username">
                <FormLabel>
                  {t("SettingsScreen.profile.fields.username.label")}
                </FormLabel>
                <FormControl asChild>
                  <Input value={data.viewer.username} disabled />
                </FormControl>
                <FormHelpText>
                  {t("SettingsScreen.profile.fields.username.help")}
                </FormHelpText>
              </FormField>
              <FormField name="avatar">
                <FormLabel>
                  {t("SettingsScreen.profile.fields.avatar.label")}
                </FormLabel>
                <img
                  src={src}
                  alt={t("SettingsScreen.profile.fields.avatar.label")}
                  className="size-16 rounded-md border border-input bg-transparent"
                />
                <FormHelpText>
                  {t("SettingsScreen.profile.fields.avatar.help")}
                </FormHelpText>
              </FormField>
              <div>
                <Button asChild>
                  <a
                    href={import.meta.env.VITE_ACDC2_ACCOUNT_SETTINGS_URL}
                    target="_blank"
                  >
                    {t("SettingsScreen.profile.buttons.edit")}{" "}
                    <Icon path={mdiOpenInNew} size="1em" />
                  </a>
                </Button>
              </div>
            </Form>

            <Separator />

            <Form className="flex flex-col gap-8">
              <div>
                <Title asChild>
                  <h2>{t("SettingsScreen.system.title")}</h2>
                </Title>
                <Label>{t("SettingsScreen.system.description")}</Label>
              </div>
              <FormField name="frontend-vcs-ref">
                <FormLabel>
                  {t("SettingsScreen.system.fields.frontendVcsRef.label")}
                </FormLabel>
                <div className="flex items-center gap-2">
                  <FormControl asChild>
                    <Input
                      value={import.meta.env.VITE_ACDC2_VCS_REF}
                      readOnly
                    />
                  </FormControl>
                  <Button variant="outline" asChild>
                    <a
                      href={import.meta.env.VITE_ACDC2_VCS_URL}
                      target="_blank"
                    >
                      {t("SettingsScreen.system.fields.frontendVcsRef.button")}
                    </a>
                  </Button>
                </div>
              </FormField>
              <FormField name="backend-vcs-ref">
                <FormLabel>
                  {t("SettingsScreen.system.fields.backendVcsRef.label")}
                </FormLabel>
                <div className="flex items-center gap-2">
                  <FormControl asChild>
                    <Input value={data.deployment.vcsRef ?? ""} readOnly />
                  </FormControl>
                  <Button variant="outline" asChild>
                    <a
                      href={data.deployment.vcsUrl ?? "#localhost"}
                      target="_blank"
                    >
                      {t("SettingsScreen.system.fields.backendVcsRef.button")}
                    </a>
                  </Button>
                </div>
              </FormField>
            </Form>
          </div>
        </div>
      </ScrollArea>
    </Scaffold>
  );
}
